/**
 * Projections workbook calculation engine.
 *
 * Pure functions only — no React, no Supabase, no path aliases — so the
 * math can be exercised in isolation (see the verification notes at the
 * bottom of the file). Every sheet in the Projections workspace (Main,
 * P&L, Balance Sheet, Cash Flow, Sales) is a *view* over the single
 * `Model` this module produces, which is what keeps the numbers on every
 * tab in lock-step with each other.
 *
 * Conventions
 *   • Months are `YYYY-MM-01` strings. `months` is a continuous range.
 *   • Money is integer cents. Units / percentages are plain numbers.
 *   • `plan` is always a number; `actual` is `null` when QuickBooks has no
 *     data for that month (future months, or months before the first sync)
 *     so the UI can render a blank instead of a misleading $0.
 *   • Actuals come ONLY from `qbo_pnl_monthly` / `qbo_balance_monthly`.
 *     The engine never lets a hand-typed number masquerade as an actual.
 *   • Cash-section rows are expressed as CASH EFFECT: + = cash in,
 *     − = cash out. For a balance-sheet account the stored input is the
 *     change in balance (delta); the engine applies the sign:
 *         asset        → effect = −delta
 *         liability    → effect = +delta
 *         equity       → effect = +delta
 */

// ---------------------------------------------------------------------------
// Input types
// ---------------------------------------------------------------------------

export type AccountType = 'asset' | 'liability' | 'equity' | 'revenue' | 'expense';

export interface CoaAccount {
  id: string;
  code: string | null;
  name: string;
  account_type: AccountType;
  subtype: string | null;
  parent_id: string | null;
  is_active: boolean;
}

export type PnlSection = 'income' | 'cogs' | 'expense' | 'other-income' | 'other-expense';
export type CashSection = 'operating' | 'investing' | 'financing';
export type BsSubSection =
  | 'bank'
  | 'ar'
  | 'other-current-asset'
  | 'fixed-asset'
  | 'other-asset'
  | 'ap'
  | 'credit-card'
  | 'other-current-liability'
  | 'long-term-liability'
  | 'equity';

export type SheetSection = PnlSection | CashSection | 'sales';

export type CustomerTier = 'wholesale_big' | 'wholesale_small' | 'retail';

export interface SalesProduct {
  id: string;
  name: string;
  revenue_account_id: string | null;
  cogs_account_id: string | null;
  price_big_cents: number;
  price_small_cents: number;
  price_retail_cents: number;
  unit_cogs_cents: number;
  wholesale_share: number;
  retail_share: number;
  position: number;
  is_active: boolean;
}

export interface SalesCustomer {
  id: string;
  name: string;
  tier: CustomerTier;
  mix_override: Record<string, number> | null;
  position: number;
  is_active: boolean;
}

export interface SalesUnitCell {
  customer_id: string;
  month: string;
  units: number;
}

export interface SheetRowDef {
  id: string;
  kind: 'account' | 'custom';
  section: string | null;
  account_id: string | null;
  after_key: string | null;
  label: string | null;
  hidden: boolean;
  value_type: string;
  rollup: 'plan' | 'memo';
}

export interface SheetCell {
  row_id: string;
  month: string;
  value: number;
}

export interface MonthAmount {
  account_id: string;
  month: string;
  amount_cents: number;
}

export interface MonthBalance {
  account_id: string;
  month: string;
  balance_cents: number;
}

export interface MonthDelta {
  account_id: string;
  month: string;
  delta_cents: number;
}

export interface CapitalItem {
  txn_date: string;
  amount_cents: number;
  direction: 'debit' | 'credit';
  description: string;
}

export interface WorkbookSettings {
  min_cash_cents: number;
  variance_pct_threshold: number;
  variance_abs_cents: number;
  gross_margin_floor_pct: number;
  fiscal_year_start_month: number;
  opening_cash_cents: number | null;
}

export const DEFAULT_SETTINGS: WorkbookSettings = {
  min_cash_cents: 10_000_00,
  variance_pct_threshold: 0.1,
  variance_abs_cents: 500_00,
  gross_margin_floor_pct: 0.35,
  fiscal_year_start_month: 1,
  opening_cash_cents: null,
};

export interface EngineInput {
  /** Continuous month range to compute. Must include every month the UI shows. */
  months: string[];
  /** First-of-month key for "today". Months before it are closed. */
  currentMonth: string;
  accounts: CoaAccount[];
  pnlActuals: MonthAmount[];
  balanceActuals: MonthBalance[];
  pnlPlans: MonthAmount[];
  balanceMovements: MonthDelta[];
  products: SalesProduct[];
  customers: SalesCustomer[];
  units: SalesUnitCell[];
  sheetRows: SheetRowDef[];
  sheetCells: SheetCell[];
  capitalItems: CapitalItem[];
  settings: WorkbookSettings;
}

// ---------------------------------------------------------------------------
// Output types
// ---------------------------------------------------------------------------

export interface Series {
  plan: number[];
  actual: (number | null)[];
}

export type RowKind =
  | 'section'
  | 'header'
  | 'account'
  | 'custom'
  | 'customer'
  | 'product'
  | 'total'
  | 'calc'
  | 'ratio'
  | 'check'
  | 'spacer';

export type ValueType = 'currency' | 'units' | 'percent';

export type PlanSource = 'manual' | 'sales' | 'cogs' | 'derived' | 'capital' | 'none';

export type Favorable = 'higher' | 'lower' | 'none';

export interface EngineRow {
  key: string;
  kind: RowKind;
  /** Logical grouping used by the sheet builders and the AI summary. */
  group: string;
  label: string;
  depth: number;
  valueType: ValueType;
  editablePlan: boolean;
  planSource: PlanSource;
  favorable: Favorable;
  accountId?: string;
  rowId?: string;
  customerId?: string;
  parentKey?: string;
  hasChildren: boolean;
  /** Secondary text shown under / beside the label (unit price, account code…). */
  hint?: string;
  series: Series;
  /** Summary over the VISIBLE months — filled by `summarise()`. */
  totals: RowTotals;
  /** True when the row carries no plan and no actual anywhere in range. */
  isEmpty: boolean;
  /** Sign the grid must apply when writing an edited cash-effect back to delta. */
  effectSign?: 1 | -1;
  /**
   * For parent-account header rows: plan cents already contributed by the
   * sub-accounts, per engine month. Typing a value on the header sets the
   * parent's own (direct) plan to `typed − planOffset` so the rollup lands on
   * the number the operator entered.
   */
  planOffset?: number[];
}

export interface RowTotals {
  plan: number;
  actual: number | null;
  /** Actual for closed months + plan for open months. */
  forecast: number;
  /** Σ(actual − plan) over closed months. */
  variance: number | null;
}

export interface CheckResult {
  key: string;
  label: string;
  description: string;
  /** Per computed month: difference that should be zero (null = n/a). */
  diffs: (number | null)[];
  /** Tolerance in cents / units below which a diff counts as passing. */
  tolerance: number;
}

export interface Ratio {
  key: string;
  label: string;
  series: Series;
  /** When set, plan/actual below this value are alarming. */
  floor?: number;
  favorable: Favorable;
}

export interface AccountNode {
  account: CoaAccount;
  children: AccountNode[];
  depth: number;
  section: PnlSection | CashSection | 'excluded';
  bsSub?: BsSubSection;
}

export interface SalesBreakdown {
  productId: string;
  unitsByTier: Record<CustomerTier, number[]>;
  units: number[];
  revenueCents: number[];
  cogsCents: number[];
  /** True when the product posts to a revenue account (reaches the P&L). */
  revenueLinked: boolean;
}

export interface Model {
  months: string[];
  currentMonth: string;
  /** Index of the first month that is not closed (current month). */
  monthIsClosed: boolean[];
  monthHasActual: boolean[];
  accountsById: Map<string, CoaAccount>;
  pnlRoots: AccountNode[];
  bsRoots: AccountNode[];
  /** Per-section P&L totals. */
  sectionTotals: Record<PnlSection, Series>;
  revenue: Series;
  grossProfit: Series;
  operatingIncome: Series;
  netIncome: Series;
  cashEffects: Record<CashSection, Series>;
  capitalEffect: Series;
  cashFlow: Series;
  openingCash: Series;
  endingCash: Series;
  bankBalance: (number | null)[];
  creditCardBalance: (number | null)[];
  sales: {
    totalUnits: Series;
    byProduct: SalesBreakdown[];
    drivenRevenue: Map<string, number[]>;
    drivenCogs: Map<string, number[]>;
    /** Planned units per customer (display series for the sales rows). */
    unitsByCustomer: Map<string, Series>;
    mixWarnings: string[];
  };
  ratios: Ratio[];
  checks: CheckResult[];
  /** Planned end-of-month balance per BS account (derived). */
  bsPlanBalances: Map<string, number[]>;
  bsActualBalances: Map<string, (number | null)[]>;
  netIncomeYtd: Series;
  retainedEarningsPlan: number[];
  hiddenWithActivity: number;
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

const zeros = (n: number) => new Array<number>(n).fill(0);
const nulls = (n: number) => new Array<number | null>(n).fill(null);

export function emptySeries(n: number): Series {
  return { plan: zeros(n), actual: nulls(n) };
}

function addSeries(into: Series, add: Series): void {
  for (let i = 0; i < into.plan.length; i++) {
    into.plan[i] += add.plan[i];
    const a = add.actual[i];
    if (a !== null) into.actual[i] = (into.actual[i] ?? 0) + a;
  }
}

function subSeries(a: Series, b: Series): Series {
  return {
    plan: a.plan.map((v, i) => v - b.plan[i]),
    actual: a.actual.map((v, i) => {
      const bv = b.actual[i];
      if (v === null && bv === null) return null;
      return (v ?? 0) - (bv ?? 0);
    }),
  };
}

function sumSeries(list: Series[], n: number): Series {
  const out = emptySeries(n);
  for (const s of list) addSeries(out, s);
  return out;
}

export function prevMonthKey(month: string): string {
  const y = Number(month.slice(0, 4));
  const m = Number(month.slice(5, 7));
  const d = new Date(Date.UTC(y, m - 2, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-01`;
}

export function monthRange(from: string, to: string): string[] {
  const out: string[] = [];
  let cur = from;
  let guard = 0;
  while (cur <= to && guard++ < 600) {
    out.push(cur);
    const y = Number(cur.slice(0, 4));
    const m = Number(cur.slice(5, 7));
    const d = new Date(Date.UTC(y, m, 1));
    cur = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-01`;
  }
  return out;
}

function roundCents(v: number): number {
  return Math.round(v);
}

// ---------------------------------------------------------------------------
// Section detection — mirrors the heuristics the Actuals tabs use so the
// projected statements line up with the QBO reports.
// ---------------------------------------------------------------------------

export function detectPnlSection(a: CoaAccount): PnlSection {
  const sub = (a.subtype ?? '').toLowerCase();
  const code = a.code ?? '';
  if (a.account_type === 'revenue') {
    if (
      sub.includes('other') ||
      sub.includes('dividend') ||
      sub.includes('interestearned') ||
      sub.includes('gainloss') ||
      sub.includes('taxexempt') ||
      sub.includes('unrealized') ||
      code.startsWith('7') ||
      code.startsWith('9')
    ) {
      return 'other-income';
    }
    return 'income';
  }
  if (
    sub.endsWith('cos') ||
    sub.includes('cogs') ||
    sub.includes('costofgoodssold') ||
    code.startsWith('5')
  ) {
    return 'cogs';
  }
  if (
    sub.includes('depreciation') ||
    sub.includes('amortization') ||
    sub.includes('othermiscellaneous') ||
    sub.includes('financecost') ||
    sub.includes('otherinterest') ||
    sub.includes('exchangegain') ||
    sub.includes('matcredit') ||
    code.startsWith('7') ||
    code.startsWith('8') ||
    code.startsWith('9')
  ) {
    return 'other-expense';
  }
  return 'expense';
}

export function detectBsSubSection(a: CoaAccount): BsSubSection {
  const sub = (a.subtype ?? '').toLowerCase();
  if (a.account_type === 'asset') {
    if (
      sub === 'bank' ||
      sub.includes('cashon') ||
      sub.includes('cashand') ||
      sub.includes('checking') ||
      sub.includes('savings') ||
      sub.includes('moneymarket')
    ) {
      return 'bank';
    }
    if (sub.includes('accountsreceivable') || sub === 'ar') return 'ar';
    if (
      sub.includes('fixedasset') ||
      sub.includes('furniture') ||
      sub.includes('vehicle') ||
      sub.includes('equipment') ||
      sub.includes('building') ||
      sub === 'land' ||
      sub.includes('leasehold') ||
      sub.includes('accumulateddepreciation')
    ) {
      return 'fixed-asset';
    }
    if (
      sub.includes('goodwill') ||
      sub.includes('intangible') ||
      sub.includes('depletable') ||
      sub.includes('leasebuyout') ||
      sub.includes('depositslongterm') ||
      sub.includes('otherlongtermassets') ||
      sub.includes('accumulatedamortizationofother')
    ) {
      return 'other-asset';
    }
    return 'other-current-asset';
  }
  if (a.account_type === 'liability') {
    if (sub.includes('accountspayable') || sub === 'ap') return 'ap';
    if (sub === 'creditcard' || sub.includes('credit card')) return 'credit-card';
    if (sub.includes('longterm') || sub.includes('notespayable') || sub.includes('mortgage')) {
      return 'long-term-liability';
    }
    return 'other-current-liability';
  }
  return 'equity';
}

export function isRetainedEarnings(a: CoaAccount): boolean {
  const sub = (a.subtype ?? '').toLowerCase();
  return sub.includes('retainedearnings') || /retained earnings/i.test(a.name);
}

/** Cash-flow statement section for a non-cash balance-sheet account. */
export function detectCashSection(a: CoaAccount, bsSub: BsSubSection): CashSection {
  if (bsSub === 'fixed-asset' || bsSub === 'other-asset') return 'investing';
  if (bsSub === 'long-term-liability' || bsSub === 'equity') return 'financing';
  const name = a.name.toLowerCase();
  if (
    /loan|note payable|line of credit|\bloc\b|financing/.test(name) &&
    a.account_type === 'liability'
  ) {
    return 'financing';
  }
  return 'operating';
}

export function cashEffectSign(type: AccountType): 1 | -1 {
  return type === 'asset' ? -1 : 1;
}

export const BS_SUB_LABEL: Record<BsSubSection, string> = {
  bank: 'Bank Accounts',
  ar: 'Accounts Receivable',
  'other-current-asset': 'Other Current Assets',
  'fixed-asset': 'Fixed Assets',
  'other-asset': 'Other Assets',
  ap: 'Accounts Payable',
  'credit-card': 'Credit Cards',
  'other-current-liability': 'Other Current Liabilities',
  'long-term-liability': 'Long-Term Liabilities',
  equity: 'Equity',
};

export const PNL_SECTION_LABEL: Record<PnlSection, string> = {
  income: 'Income',
  cogs: 'Cost of Goods Sold',
  expense: 'Operating Expenses',
  'other-income': 'Other Income',
  'other-expense': 'Other Expense',
};

export const CASH_SECTION_LABEL: Record<CashSection, string> = {
  operating: 'Operating — working capital & other balance sheet movements',
  investing: 'Investing — equipment, molds, intangibles',
  financing: 'Financing — loans, owner contributions & draws',
};

export const TIER_LABEL: Record<CustomerTier, string> = {
  wholesale_big: 'Wholesale — big volume',
  wholesale_small: 'Wholesale — small volume',
  retail: 'Retail',
};

const TIERS: CustomerTier[] = ['wholesale_big', 'wholesale_small', 'retail'];

// ---------------------------------------------------------------------------
// Tree building
// ---------------------------------------------------------------------------

function sortNodes(list: AccountNode[]): void {
  list.sort((x, y) => {
    const a = x.account.code ?? '';
    const b = y.account.code ?? '';
    if (a && b && a !== b) return a.localeCompare(b, undefined, { numeric: true });
    if (a && !b) return -1;
    if (!a && b) return 1;
    return x.account.name.localeCompare(y.account.name);
  });
  for (const n of list) sortNodes(n.children);
}

function buildTree(
  accounts: CoaAccount[],
  classify: (a: CoaAccount) => AccountNode['section'],
  bsSub?: (a: CoaAccount) => BsSubSection,
): AccountNode[] {
  const nodes = new Map<string, AccountNode>();
  for (const a of accounts) {
    nodes.set(a.id, {
      account: a,
      children: [],
      depth: 0,
      section: classify(a),
      bsSub: bsSub ? bsSub(a) : undefined,
    });
  }
  const roots: AccountNode[] = [];
  for (const n of nodes.values()) {
    const parent = n.account.parent_id ? nodes.get(n.account.parent_id) : undefined;
    if (parent && parent !== n) parent.children.push(n);
    else roots.push(n);
  }
  // Children inherit the root's section / sub-section so one misclassified
  // leaf cannot break the statement layout.
  const stamp = (
    n: AccountNode,
    depth: number,
    section: AccountNode['section'],
    sub?: BsSubSection,
  ) => {
    n.depth = depth;
    n.section = section;
    if (sub) n.bsSub = sub;
    for (const c of n.children) stamp(c, depth + 1, section, sub);
  };
  for (const r of roots) stamp(r, 0, r.section, r.bsSub);
  sortNodes(roots);
  return roots;
}

function walk(nodes: AccountNode[], fn: (n: AccountNode) => void): void {
  for (const n of nodes) {
    fn(n);
    walk(n.children, fn);
  }
}

// ---------------------------------------------------------------------------
// Main entry point
// ---------------------------------------------------------------------------

export function computeModel(input: EngineInput): Model {
  const { months, currentMonth, settings } = input;
  const n = months.length;
  const idx = new Map<string, number>(months.map((m, i) => [m, i]));

  const accountsById = new Map(input.accounts.map((a) => [a.id, a]));

  // ---- Which months have QBO data at all? ----
  const actualMonthSet = new Set<string>();
  for (const r of input.pnlActuals) actualMonthSet.add(r.month);
  for (const r of input.balanceActuals) actualMonthSet.add(r.month);
  const monthHasActual = months.map((m) => actualMonthSet.has(m));
  const monthIsClosed = months.map((m) => m < currentMonth);

  // ---- Lookups ----
  const pnlActual = new Map<string, Map<string, number>>();
  for (const r of input.pnlActuals) {
    let inner = pnlActual.get(r.account_id);
    if (!inner) pnlActual.set(r.account_id, (inner = new Map()));
    inner.set(r.month, (inner.get(r.month) ?? 0) + r.amount_cents);
  }
  const pnlPlan = new Map<string, Map<string, number>>();
  for (const r of input.pnlPlans) {
    let inner = pnlPlan.get(r.account_id);
    if (!inner) pnlPlan.set(r.account_id, (inner = new Map()));
    inner.set(r.month, r.amount_cents);
  }
  const balActual = new Map<string, Map<string, number>>();
  for (const r of input.balanceActuals) {
    let inner = balActual.get(r.account_id);
    if (!inner) balActual.set(r.account_id, (inner = new Map()));
    inner.set(r.month, r.balance_cents);
  }
  const balMove = new Map<string, Map<string, number>>();
  for (const r of input.balanceMovements) {
    let inner = balMove.get(r.account_id);
    if (!inner) balMove.set(r.account_id, (inner = new Map()));
    inner.set(r.month, r.delta_cents);
  }

  // ---- Sales model ----
  const sales = computeSales(input, months, idx);

  // ---- P&L tree ----
  const pnlAccounts = input.accounts.filter(
    (a) => a.account_type === 'revenue' || a.account_type === 'expense',
  );
  const pnlRoots = buildTree(pnlAccounts, (a) => detectPnlSection(a));

  /** Own (direct) series for a P&L account. */
  const pnlOwn = new Map<string, Series>();
  walk(pnlRoots, (node) => {
    const a = node.account;
    const s = emptySeries(n);
    const driven = sales.drivenRevenue.get(a.id) ?? sales.drivenCogs.get(a.id);
    for (let i = 0; i < n; i++) {
      const m = months[i];
      s.plan[i] = driven ? driven[i] : (pnlPlan.get(a.id)?.get(m) ?? 0);
      s.actual[i] = monthHasActual[i] ? (pnlActual.get(a.id)?.get(m) ?? 0) : null;
    }
    pnlOwn.set(a.id, s);
  });

  /** Subtree total (own + descendants). */
  const pnlTotal = new Map<string, Series>();
  const rollPnl = (node: AccountNode): Series => {
    const s = emptySeries(n);
    addSeries(s, pnlOwn.get(node.account.id)!);
    for (const c of node.children) addSeries(s, rollPnl(c));
    pnlTotal.set(node.account.id, s);
    return s;
  };
  for (const r of pnlRoots) rollPnl(r);

  // Custom rows that roll into a P&L section plan.
  const customBySection = new Map<string, Series>();
  const cellsByRow = new Map<string, Map<string, number>>();
  for (const c of input.sheetCells) {
    let inner = cellsByRow.get(c.row_id);
    if (!inner) cellsByRow.set(c.row_id, (inner = new Map()));
    inner.set(c.month, c.value);
  }
  const customSeries = new Map<string, Series>();
  for (const row of input.sheetRows) {
    if (row.kind !== 'custom') continue;
    const s = emptySeries(n);
    const cells = cellsByRow.get(row.id);
    const isCurrency = row.value_type === 'currency';
    for (let i = 0; i < n; i++) {
      const v = cells?.get(months[i]) ?? 0;
      s.plan[i] = isCurrency ? roundCents(v * 100) : v;
    }
    customSeries.set(row.id, s);
    if (row.rollup === 'plan' && isCurrency && row.section) {
      let agg = customBySection.get(row.section);
      if (!agg) customBySection.set(row.section, (agg = emptySeries(n)));
      for (let i = 0; i < n; i++) agg.plan[i] += s.plan[i];
    }
  }

  const sectionTotals: Record<PnlSection, Series> = {
    income: emptySeries(n),
    cogs: emptySeries(n),
    expense: emptySeries(n),
    'other-income': emptySeries(n),
    'other-expense': emptySeries(n),
  };
  for (const r of pnlRoots) {
    if (r.section === 'excluded') continue;
    addSeries(sectionTotals[r.section as PnlSection], pnlTotal.get(r.account.id)!);
  }
  for (const sec of Object.keys(sectionTotals) as PnlSection[]) {
    const extra = customBySection.get(sec);
    if (extra) for (let i = 0; i < n; i++) sectionTotals[sec].plan[i] += extra.plan[i];
    // A section with QBO data in a month but no postings is a true $0.
    for (let i = 0; i < n; i++) {
      if (monthHasActual[i] && sectionTotals[sec].actual[i] === null)
        sectionTotals[sec].actual[i] = 0;
    }
  }

  const revenue = sectionTotals.income;
  const grossProfit = subSeries(revenue, sectionTotals.cogs);
  const operatingIncome = subSeries(grossProfit, sectionTotals.expense);
  const otherNet = subSeries(sectionTotals['other-income'], sectionTotals['other-expense']);
  const netIncome = {
    plan: operatingIncome.plan.map((v, i) => v + otherNet.plan[i]),
    actual: operatingIncome.actual.map((v, i) => {
      const o = otherNet.actual[i];
      if (v === null && o === null) return null;
      return (v ?? 0) + (o ?? 0);
    }),
  };

  // ---- Balance sheet tree ----
  const bsAccounts = input.accounts.filter(
    (a) =>
      a.account_type === 'asset' || a.account_type === 'liability' || a.account_type === 'equity',
  );
  const bsRoots = buildTree(
    bsAccounts,
    (a) => {
      const sub = detectBsSubSection(a);
      if (sub === 'bank' || isRetainedEarnings(a)) return 'excluded';
      return detectCashSection(a, sub);
    },
    detectBsSubSection,
  );

  const bsActualBalances = new Map<string, (number | null)[]>();
  const bsPlanBalances = new Map<string, number[]>();
  /** Cash effect per BS account (leaf-level own movement). */
  const bsEffect = new Map<string, Series>();
  const bsIsBank = new Set<string>();
  const bsIsCreditCard = new Set<string>();
  const bsIsRe = new Set<string>();

  const bankBalance = nulls(n);
  const creditCardBalance = nulls(n);

  // Latest month with any balance data before each computed month — used
  // to find the "prior" balance for the first month in range.
  const balanceAt = (accountId: string, m: string): number | null => {
    if (!actualMonthSet.has(m)) return null;
    return balActual.get(accountId)?.get(m) ?? 0;
  };

  walk(bsRoots, (node) => {
    const a = node.account;
    const isBank = node.bsSub === 'bank';
    const isCc = node.bsSub === 'credit-card';
    const isRe = isRetainedEarnings(a);
    if (isBank) bsIsBank.add(a.id);
    if (isCc) bsIsCreditCard.add(a.id);
    if (isRe) bsIsRe.add(a.id);

    const actual = nulls(n);
    for (let i = 0; i < n; i++) actual[i] = balanceAt(a.id, months[i]);
    bsActualBalances.set(a.id, actual);

    const eff = emptySeries(n);
    const sign = cashEffectSign(a.account_type);
    const plan = zeros(n);
    for (let i = 0; i < n; i++) {
      const m = months[i];
      const prevM = prevMonthKey(m);
      const prevActual = i > 0 ? actual[i - 1] : balanceAt(a.id, prevM);
      const delta = balMove.get(a.id)?.get(m) ?? 0;
      eff.plan[i] = sign * delta;
      const cur = actual[i];
      if (cur !== null) {
        eff.actual[i] = sign * (cur - (prevActual ?? 0));
      }
      const base = prevActual !== null ? prevActual : i > 0 ? plan[i - 1] : 0;
      plan[i] = base + delta;
    }
    bsEffect.set(a.id, eff);
    bsPlanBalances.set(a.id, plan);

    if (isBank) {
      for (let i = 0; i < n; i++) {
        const v = actual[i];
        if (v !== null) bankBalance[i] = (bankBalance[i] ?? 0) + v;
      }
    }
    if (isCc) {
      for (let i = 0; i < n; i++) {
        const v = actual[i];
        if (v !== null) creditCardBalance[i] = (creditCardBalance[i] ?? 0) + v;
      }
    }
  });

  const cashEffects: Record<CashSection, Series> = {
    operating: emptySeries(n),
    investing: emptySeries(n),
    financing: emptySeries(n),
  };
  walk(bsRoots, (node) => {
    if (node.section === 'excluded') return;
    addSeries(cashEffects[node.section as CashSection], bsEffect.get(node.account.id)!);
  });
  for (const sec of ['operating', 'investing', 'financing'] as CashSection[]) {
    const extra = customBySection.get(sec);
    if (extra) for (let i = 0; i < n; i++) cashEffects[sec].plan[i] += extra.plan[i];
    for (let i = 0; i < n; i++) {
      if (monthHasActual[i] && cashEffects[sec].actual[i] === null) cashEffects[sec].actual[i] = 0;
    }
  }

  // ---- Capital plan items (plan only) ----
  const capitalEffect = emptySeries(n);
  for (const item of input.capitalItems) {
    const m = item.txn_date.slice(0, 7) + '-01';
    const i = idx.get(m);
    if (i === undefined) continue;
    capitalEffect.plan[i] += item.direction === 'credit' ? item.amount_cents : -item.amount_cents;
  }

  // ---- Cash flow & projected cash ----
  const cashFlow = emptySeries(n);
  for (let i = 0; i < n; i++) {
    cashFlow.plan[i] =
      netIncome.plan[i] +
      cashEffects.operating.plan[i] +
      cashEffects.investing.plan[i] +
      cashEffects.financing.plan[i] +
      capitalEffect.plan[i];
    const ni = netIncome.actual[i];
    if (ni !== null) {
      cashFlow.actual[i] =
        ni +
        (cashEffects.operating.actual[i] ?? 0) +
        (cashEffects.investing.actual[i] ?? 0) +
        (cashEffects.financing.actual[i] ?? 0);
    }
  }

  const openingCash = emptySeries(n);
  const endingCash = emptySeries(n);
  // Opening cash for the first month: QBO bank balance at the prior month
  // end; fall back to the operator's override when QBO has nothing.
  const firstPrev = prevMonthKey(months[0]);
  let openingFromQbo: number | null = null;
  if (actualMonthSet.has(firstPrev)) {
    openingFromQbo = 0;
    for (const id of bsIsBank) openingFromQbo += balActual.get(id)?.get(firstPrev) ?? 0;
  }
  for (let i = 0; i < n; i++) {
    const prevActualBank = i > 0 ? bankBalance[i - 1] : openingFromQbo;
    const openPlan =
      prevActualBank !== null
        ? prevActualBank
        : i > 0
          ? endingCash.plan[i - 1]
          : (settings.opening_cash_cents ?? 0);
    openingCash.plan[i] = openPlan;
    openingCash.actual[i] = prevActualBank;
    endingCash.plan[i] = openPlan + cashFlow.plan[i];
    endingCash.actual[i] = bankBalance[i];
  }

  // ---- Net income YTD + retained earnings (for the projected balance sheet) ----
  const fyStart = Math.min(12, Math.max(1, settings.fiscal_year_start_month || 1));
  const pnlActualTotalByMonth = new Map<string, number>();
  for (const r of input.pnlActuals) {
    const a = accountsById.get(r.account_id);
    if (!a) continue;
    const sign = a.account_type === 'revenue' ? 1 : -1;
    pnlActualTotalByMonth.set(
      r.month,
      (pnlActualTotalByMonth.get(r.month) ?? 0) + sign * r.amount_cents,
    );
  }
  const fiscalYearStartFor = (m: string): string => {
    const y = Number(m.slice(0, 4));
    const mm = Number(m.slice(5, 7));
    const startYear = mm >= fyStart ? y : y - 1;
    return `${startYear}-${String(fyStart).padStart(2, '0')}-01`;
  };
  const netIncomeYtd = emptySeries(n);
  const retainedEarningsPlan = zeros(n);
  let reCarry = 0;
  for (let i = 0; i < n; i++) {
    const m = months[i];
    const fyS = fiscalYearStartFor(m);
    // Actual YTD: sum QBO P&L from fiscal-year start to m.
    if (monthHasActual[i]) {
      let acc = 0;
      for (const k of monthRange(fyS, m)) acc += pnlActualTotalByMonth.get(k) ?? 0;
      netIncomeYtd.actual[i] = acc;
    }
    // Plan YTD follows the same chain rule as the bank balance: start from
    // the prior month's ACTUAL year-to-date when QBO has it (otherwise the
    // prior month's plan), then apply this month's planned net income.
    let prevYtd: number;
    if (m === fyS) {
      prevYtd = 0;
    } else if (i > 0) {
      const prevActual = netIncomeYtd.actual[i - 1];
      prevYtd = prevActual !== null ? prevActual : netIncomeYtd.plan[i - 1];
    } else {
      prevYtd = 0;
      for (const k of monthRange(fyS, prevMonthKey(m)))
        prevYtd += pnlActualTotalByMonth.get(k) ?? 0;
    }
    netIncomeYtd.plan[i] = prevYtd + netIncome.plan[i];

    // Retained earnings: actual balance when QBO has it; otherwise carry
    // forward, rolling the prior fiscal year's net income in at FY start.
    let reActual: number | null = null;
    if (monthHasActual[i]) {
      reActual = 0;
      for (const id of bsIsRe) reActual += balActual.get(id)?.get(m) ?? 0;
    }
    if (reActual !== null) {
      reCarry = reActual;
    } else if (i === 0) {
      let prior = 0;
      for (const id of bsIsRe) prior += balActual.get(id)?.get(firstPrev) ?? 0;
      if (m === fyS) {
        // Fiscal year rolled over right before the range: last year's net
        // income (from QBO) closes into retained earnings.
        for (const k of monthRange(fiscalYearStartFor(firstPrev), firstPrev)) {
          prior += pnlActualTotalByMonth.get(k) ?? 0;
        }
      }
      reCarry = prior;
    } else if (m === fyS) {
      reCarry += netIncomeYtd.plan[i - 1];
    }
    retainedEarningsPlan[i] = reCarry;
  }

  // ---- Ratios ----
  const ratios = buildRatios(
    pnlRoots,
    pnlTotal,
    revenue,
    grossProfit,
    netIncome,
    sectionTotals,
    settings,
  );

  // ---- Checks ----
  const checks = buildChecks({
    n,
    months,
    monthHasActual,
    pnlRoots,
    pnlOwn,
    pnlTotal,
    sectionTotals,
    customBySection,
    netIncome,
    cashFlow,
    bankBalance,
    openingFromQbo,
    bsRoots,
    bsActualBalances,
    netIncomeYtd,
    sales,
    customers: input.customers,
  });

  // ---- Hidden rows carrying real numbers (surfaced in the UI) ----
  let hiddenWithActivity = 0;
  for (const row of input.sheetRows) {
    if (row.kind !== 'account' || !row.hidden || !row.account_id) continue;
    const s = pnlTotal.get(row.account_id) ?? bsEffect.get(row.account_id);
    if (s && (s.plan.some((v) => v !== 0) || s.actual.some((v) => v !== null && v !== 0))) {
      hiddenWithActivity++;
    }
  }

  const model: Model = {
    months,
    currentMonth,
    monthIsClosed,
    monthHasActual,
    accountsById,
    pnlRoots,
    bsRoots,
    sectionTotals,
    revenue,
    grossProfit,
    operatingIncome,
    netIncome,
    cashEffects,
    capitalEffect,
    cashFlow,
    openingCash,
    endingCash,
    bankBalance,
    creditCardBalance,
    sales,
    ratios,
    checks,
    bsPlanBalances,
    bsActualBalances,
    netIncomeYtd,
    retainedEarningsPlan,
    hiddenWithActivity,
  };

  // Stash the per-account series on the model via closures for the sheet
  // builders (kept off the public type to avoid leaking internals).
  internals.set(model, {
    pnlOwn,
    pnlTotal,
    bsEffect,
    customSeries,
    bsIsBank,
    bsIsCreditCard,
    bsIsRe,
  });
  return model;
}

interface Internals {
  pnlOwn: Map<string, Series>;
  pnlTotal: Map<string, Series>;
  bsEffect: Map<string, Series>;
  customSeries: Map<string, Series>;
  bsIsBank: Set<string>;
  bsIsCreditCard: Set<string>;
  bsIsRe: Set<string>;
}
const internals = new WeakMap<Model, Internals>();

// ---------------------------------------------------------------------------
// Sales model
// ---------------------------------------------------------------------------

function computeSales(
  input: EngineInput,
  months: string[],
  idx: Map<string, number>,
): Model['sales'] {
  const n = months.length;
  const products = input.products
    .filter((p) => p.is_active)
    .sort((a, b) => a.position - b.position);
  const customers = input.customers.filter((c) => c.is_active);

  const unitsByCustomer = new Map<string, number[]>();
  for (const c of customers) unitsByCustomer.set(c.id, zeros(n));
  for (const u of input.units) {
    const i = idx.get(u.month);
    const arr = unitsByCustomer.get(u.customer_id);
    if (i !== undefined && arr) arr[i] += Number(u.units) || 0;
  }

  const totalUnits = emptySeries(n);
  for (const arr of unitsByCustomer.values())
    for (let i = 0; i < n; i++) totalUnits.plan[i] += arr[i];

  const mixWarnings: string[] = [];
  const shareFor = (c: SalesCustomer, p: SalesProduct): number => {
    if (c.mix_override && Object.keys(c.mix_override).length > 0) {
      return Number(c.mix_override[p.id] ?? 0) || 0;
    }
    return c.tier === 'retail' ? Number(p.retail_share) || 0 : Number(p.wholesale_share) || 0;
  };
  for (const c of customers) {
    const sum = products.reduce((s, p) => s + shareFor(c, p), 0);
    if (products.length > 0 && Math.abs(sum - 1) > 0.001) {
      mixWarnings.push(`${c.name}: model mix sums to ${(sum * 100).toFixed(0)}% (expected 100%)`);
    }
  }

  const byProduct: SalesBreakdown[] = [];
  const drivenRevenue = new Map<string, number[]>();
  const drivenCogs = new Map<string, number[]>();
  for (const p of products) {
    const unitsByTier: Record<CustomerTier, number[]> = {
      wholesale_big: zeros(n),
      wholesale_small: zeros(n),
      retail: zeros(n),
    };
    for (const c of customers) {
      const share = shareFor(c, p);
      if (!share) continue;
      const arr = unitsByCustomer.get(c.id)!;
      for (let i = 0; i < n; i++) unitsByTier[c.tier][i] += arr[i] * share;
    }
    const units = zeros(n);
    const revenueCents = zeros(n);
    const cogsCents = zeros(n);
    const price: Record<CustomerTier, number> = {
      wholesale_big: p.price_big_cents,
      wholesale_small: p.price_small_cents,
      retail: p.price_retail_cents,
    };
    for (let i = 0; i < n; i++) {
      let rev = 0;
      let u = 0;
      for (const t of TIERS) {
        u += unitsByTier[t][i];
        rev += unitsByTier[t][i] * price[t];
      }
      units[i] = u;
      revenueCents[i] = roundCents(rev);
      cogsCents[i] = roundCents(u * p.unit_cogs_cents);
    }
    byProduct.push({
      productId: p.id,
      unitsByTier,
      units,
      revenueCents,
      cogsCents,
      revenueLinked: !!p.revenue_account_id,
    });
    if (p.revenue_account_id) {
      const prev = drivenRevenue.get(p.revenue_account_id) ?? zeros(n);
      drivenRevenue.set(
        p.revenue_account_id,
        prev.map((v, i) => v + revenueCents[i]),
      );
    }
    if (p.cogs_account_id) {
      const prev = drivenCogs.get(p.cogs_account_id) ?? zeros(n);
      drivenCogs.set(
        p.cogs_account_id,
        prev.map((v, i) => v + cogsCents[i]),
      );
    }
  }

  const unitsSeries = new Map<string, Series>();
  for (const [id, arr] of unitsByCustomer) unitsSeries.set(id, { plan: arr, actual: nulls(n) });

  return {
    totalUnits,
    byProduct,
    drivenRevenue,
    drivenCogs,
    unitsByCustomer: unitsSeries,
    mixWarnings,
  };
}

// ---------------------------------------------------------------------------
// Ratios
// ---------------------------------------------------------------------------

function pctSeries(num: Series, den: Series): Series {
  return {
    plan: num.plan.map((v, i) => (den.plan[i] ? v / den.plan[i] : 0)),
    actual: num.actual.map((v, i) => {
      const d = den.actual[i];
      if (v === null || d === null || d === 0) return null;
      return v / d;
    }),
  };
}

function buildRatios(
  pnlRoots: AccountNode[],
  pnlTotal: Map<string, Series>,
  revenue: Series,
  grossProfit: Series,
  netIncome: Series,
  sectionTotals: Record<PnlSection, Series>,
  settings: WorkbookSettings,
): Ratio[] {
  const ratios: Ratio[] = [
    {
      key: 'ratio:gross-margin',
      label: 'Gross margin %',
      series: pctSeries(grossProfit, revenue),
      floor: settings.gross_margin_floor_pct,
      favorable: 'higher',
    },
    {
      key: 'ratio:cogs',
      label: 'COGS % of revenue',
      series: pctSeries(sectionTotals.cogs, revenue),
      favorable: 'lower',
    },
    {
      key: 'ratio:opex',
      label: 'Operating expenses % of revenue',
      series: pctSeries(sectionTotals.expense, revenue),
      favorable: 'lower',
    },
  ];
  for (const root of pnlRoots) {
    if (root.section !== 'expense') continue;
    const s = pnlTotal.get(root.account.id)!;
    if (!s.plan.some((v) => v) && !s.actual.some((v) => v)) continue;
    ratios.push({
      key: `ratio:acct:${root.account.id}`,
      label: `${root.account.name} % of revenue`,
      series: pctSeries(s, revenue),
      favorable: 'lower',
    });
  }
  ratios.push({
    key: 'ratio:net-margin',
    label: 'Net margin %',
    series: pctSeries(netIncome, revenue),
    favorable: 'higher',
  });
  return ratios;
}

// ---------------------------------------------------------------------------
// Integrity checks — three independent recomputations that must agree.
// ---------------------------------------------------------------------------

interface CheckCtx {
  n: number;
  months: string[];
  monthHasActual: boolean[];
  pnlRoots: AccountNode[];
  pnlOwn: Map<string, Series>;
  pnlTotal: Map<string, Series>;
  sectionTotals: Record<PnlSection, Series>;
  customBySection: Map<string, Series>;
  netIncome: Series;
  cashFlow: Series;
  bankBalance: (number | null)[];
  openingFromQbo: number | null;
  bsRoots: AccountNode[];
  bsActualBalances: Map<string, (number | null)[]>;
  netIncomeYtd: Series;
  sales: Model['sales'];
  customers: SalesCustomer[];
}

function buildChecks(ctx: CheckCtx): CheckResult[] {
  const { n } = ctx;
  const checks: CheckResult[] = [];

  // 1. Roll-up integrity: flat sum of every leaf posting (independent path)
  //    must equal the tree-derived Net Income.
  {
    const diffs: (number | null)[] = nulls(n);
    const flatPlan = zeros(n);
    const flatActual = zeros(n);
    walk(ctx.pnlRoots, (node) => {
      if (node.section === 'excluded') return;
      const own = ctx.pnlOwn.get(node.account.id)!;
      const sign = node.account.account_type === 'revenue' ? 1 : -1;
      for (let i = 0; i < n; i++) {
        flatPlan[i] += sign * own.plan[i];
        flatActual[i] += sign * (own.actual[i] ?? 0);
      }
    });
    for (const [sec, s] of ctx.customBySection) {
      const sign =
        sec === 'income' || sec === 'other-income'
          ? 1
          : sec === 'cogs' || sec === 'expense' || sec === 'other-expense'
            ? -1
            : 0;
      if (!sign) continue;
      for (let i = 0; i < n; i++) flatPlan[i] += sign * s.plan[i];
    }
    for (let i = 0; i < n; i++) {
      const planDiff = flatPlan[i] - ctx.netIncome.plan[i];
      const ni = ctx.netIncome.actual[i];
      const actualDiff = ni === null ? 0 : flatActual[i] - ni;
      diffs[i] = Math.abs(planDiff) + Math.abs(actualDiff);
    }
    checks.push({
      key: 'check:rollup',
      label: 'P&L roll-up integrity',
      description:
        'Sum of every individual account (plan and actual) recomputed from scratch equals the Net Income shown on the statement.',
      diffs,
      tolerance: 0,
    });
  }

  // 2. Cash reconciliation (actual months): Net income + non-cash balance
  //    sheet movements must equal the change in bank balances from QBO.
  {
    const diffs: (number | null)[] = nulls(n);
    for (let i = 0; i < n; i++) {
      const flow = ctx.cashFlow.actual[i];
      const bank = ctx.bankBalance[i];
      const prevBank = i > 0 ? ctx.bankBalance[i - 1] : ctx.openingFromQbo;
      if (flow === null || bank === null || prevBank === null) continue;
      diffs[i] = bank - prevBank - flow;
    }
    checks.push({
      key: 'check:cash',
      label: 'Cash reconciliation (QuickBooks)',
      description:
        'Actual net income plus every actual balance-sheet movement equals the actual change in bank balances. A gap means QuickBooks has an unbalanced or uncategorised entry.',
      diffs,
      tolerance: 100,
    });
  }

  // 3. Balance sheet balances (actual): Assets = Liabilities + Equity + Net income YTD.
  {
    const diffs: (number | null)[] = nulls(n);
    for (let i = 0; i < n; i++) {
      if (!ctx.monthHasActual[i]) continue;
      let assets = 0;
      let liabEq = 0;
      walk(ctx.bsRoots, (node) => {
        const v = ctx.bsActualBalances.get(node.account.id)?.[i] ?? 0;
        if (node.account.account_type === 'asset') assets += v;
        else liabEq += v;
      });
      const ni = ctx.netIncomeYtd.actual[i] ?? 0;
      diffs[i] = assets - liabEq - ni;
    }
    checks.push({
      key: 'check:balance',
      label: 'Balance sheet balances',
      description:
        'Total assets equal total liabilities plus equity plus fiscal-year-to-date net income for every month QuickBooks has reported.',
      diffs,
      tolerance: 100,
    });
  }

  // 4. Sales model: every customer's model mix sums to 100%, and the
  //    revenue pushed into the P&L equals the per-product breakdown.
  {
    const diffs: (number | null)[] = nulls(n);
    const mixBad = ctx.sales.mixWarnings.length;
    for (let i = 0; i < n; i++) {
      // Products without a linked revenue account legitimately never reach
      // the P&L, so only linked products take part in the comparison.
      let fromProducts = 0;
      for (const p of ctx.sales.byProduct) if (p.revenueLinked) fromProducts += p.revenueCents[i];
      let fromAccounts = 0;
      for (const arr of ctx.sales.drivenRevenue.values()) fromAccounts += arr[i];
      diffs[i] = Math.abs(fromProducts - fromAccounts) + mixBad;
    }
    checks.push({
      key: 'check:sales',
      label: 'Sales model consistency',
      description:
        "Every customer's model mix adds to 100% and the unit-driven revenue flowing into the revenue accounts equals the per-product breakdown.",
      diffs,
      tolerance: 0,
    });
  }

  return checks;
}

// ---------------------------------------------------------------------------
// Sheet builders — turn the model into ordered rows for the grid.
// ---------------------------------------------------------------------------

export type SheetId = 'main' | 'pnl' | 'balance-sheet' | 'cashflow' | 'sales';

export interface SheetBuildOptions {
  sheet: SheetId;
  /** Months actually shown — used for totals and for the visibility rule. */
  visibleMonths: string[];
  sheetRows: SheetRowDef[];
  products: SalesProduct[];
  customers: SalesCustomer[];
  /** Show every account, even ones with no activity. */
  showAllAccounts?: boolean;
}

export function buildSheet(model: Model, opts: SheetBuildOptions): EngineRow[] {
  const rows: EngineRow[] =
    opts.sheet === 'balance-sheet'
      ? buildBalanceSheetRows(model, opts)
      : opts.sheet === 'cashflow'
        ? buildCashflowRows(model, opts)
        : buildMainRows(model, opts);
  const visIdx = opts.visibleMonths.map((m) => model.months.indexOf(m)).filter((i) => i >= 0);
  // Balance-sheet rows are point-in-time balances — summing them across
  // months is meaningless, so their "totals" are end-of-range snapshots.
  const pointInTime = opts.sheet === 'balance-sheet';
  for (const r of rows) summarise(r, model, visIdx, pointInTime);
  return rows;
}

function summarise(row: EngineRow, model: Model, visIdx: number[], pointInTime = false): void {
  if (pointInTime && row.valueType !== 'percent') {
    const last = visIdx[visIdx.length - 1];
    const plan = last === undefined ? 0 : row.series.plan[last];
    let actual: number | null = null;
    let variance: number | null = null;
    // Latest closed month in range that QuickBooks has a balance for.
    for (let k = visIdx.length - 1; k >= 0; k--) {
      const i = visIdx[k];
      const a = row.series.actual[i];
      if (a !== null && model.monthIsClosed[i]) {
        actual = a;
        variance = a - row.series.plan[i];
        break;
      }
    }
    const lastActual = last === undefined ? null : row.series.actual[last];
    const forecast =
      last !== undefined && model.monthIsClosed[last] && lastActual !== null ? lastActual : plan;
    row.totals = { plan, actual, forecast, variance };
    row.isEmpty = row.series.plan.every((v) => !v) && row.series.actual.every((v) => !v);
    return;
  }
  let plan = 0;
  let actual: number | null = null;
  let forecast = 0;
  let variance: number | null = null;
  let any = false;
  for (const i of visIdx) {
    const p = row.series.plan[i] ?? 0;
    const a = row.series.actual[i] ?? null;
    plan += p;
    if (a !== null) {
      actual = (actual ?? 0) + a;
      if (model.monthIsClosed[i]) variance = (variance ?? 0) + (a - p);
    }
    forecast += model.monthIsClosed[i] && a !== null ? a : p;
    if (p !== 0 || (a !== null && a !== 0)) any = true;
  }
  if (row.valueType === 'percent') {
    // Percentages don't sum — report the last visible month instead.
    const last = visIdx[visIdx.length - 1];
    plan = last === undefined ? 0 : row.series.plan[last];
    actual = last === undefined ? null : row.series.actual[last];
    forecast = plan;
    variance = actual === null ? null : actual - plan;
  }
  row.totals = { plan, actual, forecast, variance };
  row.isEmpty = !any && row.series.plan.every((v) => !v) && row.series.actual.every((v) => !v);
}

/**
 * Collapsed sections keep their depth-0 total / calc lines (e.g. "Total
 * Income", "Gross profit") so the headline number stays readable.
 */
export function isSectionSummaryRow(r: EngineRow): boolean {
  return r.depth === 0 && (r.kind === 'total' || r.kind === 'calc');
}

/** Whether a `sec:*` row has anything underneath that folding would hide. */
export function sectionIsCollapsible(rows: EngineRow[], sectionKey: string): boolean {
  const start = rows.findIndex((r) => r.key === sectionKey);
  if (start < 0) return false;
  for (let i = start + 1; i < rows.length; i++) {
    const r = rows[i];
    if (r.kind === 'section' || r.kind === 'spacer') return false;
    if (!isSectionSummaryRow(r)) return true;
  }
  return false;
}

function baseRow(
  partial: Partial<EngineRow> & Pick<EngineRow, 'key' | 'kind' | 'group' | 'label' | 'series'>,
): EngineRow {
  return {
    depth: 0,
    valueType: 'currency',
    editablePlan: false,
    planSource: 'none',
    favorable: 'none',
    hasChildren: false,
    totals: { plan: 0, actual: null, forecast: 0, variance: null },
    isEmpty: false,
    ...partial,
  };
}

const sectionRow = (key: string, group: string, label: string, n: number): EngineRow =>
  baseRow({ key, kind: 'section', group, label, series: emptySeries(n) });

const spacerRow = (key: string, n: number): EngineRow =>
  baseRow({ key, kind: 'spacer', group: 'spacer', label: '', series: emptySeries(n) });

function hasActivity(s: Series, visIdx: number[]): boolean {
  for (const i of visIdx) {
    if (s.plan[i]) return true;
    const a = s.actual[i];
    if (a !== null && a !== 0) return true;
  }
  return false;
}

function buildMainRows(model: Model, opts: SheetBuildOptions): EngineRow[] {
  const n = model.months.length;
  const visIdx = opts.visibleMonths.map((m) => model.months.indexOf(m)).filter((i) => i >= 0);
  const intr = internals.get(model)!;
  const rows: EngineRow[] = [];
  const includeSales = opts.sheet === 'main' || opts.sheet === 'sales';
  const includePnl = opts.sheet === 'main' || opts.sheet === 'pnl';
  const includeCash = opts.sheet === 'main';

  const overrides = new Map<string, SheetRowDef>();
  const customsByAnchor = new Map<string, SheetRowDef[]>();
  for (const r of opts.sheetRows) {
    if (r.kind === 'account' && r.account_id) overrides.set(r.account_id, r);
    if (r.kind === 'custom') {
      const anchor = r.after_key ?? `end:${r.section ?? ''}`;
      const list = customsByAnchor.get(anchor) ?? [];
      list.push(r);
      customsByAnchor.set(anchor, list);
    }
  }
  const emitted = new Set<string>();
  const emitCustoms = (anchorKey: string, group: string, depth: number) => {
    const list = customsByAnchor.get(anchorKey);
    if (!list) return;
    for (const c of list) {
      if (emitted.has(c.id)) continue;
      emitted.add(c.id);
      const key = `row:${c.id}`;
      const vt: ValueType =
        c.value_type === 'units' ? 'units' : c.value_type === 'percent' ? 'percent' : 'currency';
      const isCashGroup = group === 'operating' || group === 'investing' || group === 'financing';
      rows.push(
        baseRow({
          key,
          kind: 'custom',
          group,
          label: c.label ?? 'Custom line',
          depth,
          valueType: vt,
          editablePlan: true,
          planSource: 'manual',
          favorable:
            vt !== 'currency' || c.rollup === 'memo'
              ? 'none'
              : group === 'income' || group === 'other-income' || isCashGroup
                ? 'higher'
                : 'lower',
          rowId: c.id,
          hint:
            c.rollup === 'memo'
              ? 'Memo line — not included in totals'
              : 'Manual line — not linked to QuickBooks',
          series: intr.customSeries.get(c.id) ?? emptySeries(n),
        }),
      );
      emitCustoms(key, group, depth);
    }
  };
  const emitSectionTail = (section: string, depth: number) =>
    emitCustoms(`end:${section}`, section, depth);

  // ---------------- Sales projections ----------------
  if (includeSales) {
    rows.push(sectionRow('sec:sales', 'sales', 'Sales projections — units by customer', n));
    const customers = opts.customers
      .filter((c) => c.is_active)
      .sort((a, b) => a.position - b.position);
    for (const c of customers) {
      const series = model.sales.unitsByCustomer.get(c.id) ?? emptySeries(n);
      rows.push(
        baseRow({
          key: `cust:${c.id}`,
          kind: 'customer',
          group: 'sales',
          label: c.name,
          depth: 1,
          valueType: 'units',
          editablePlan: true,
          planSource: 'manual',
          favorable: 'higher',
          customerId: c.id,
          hint: c.mix_override ? `${TIER_LABEL[c.tier]} · custom model mix` : TIER_LABEL[c.tier],
          series,
        }),
      );
      emitCustoms(`cust:${c.id}`, 'sales', 1);
    }
    emitSectionTail('sales', 1);
    rows.push(
      baseRow({
        key: 'calc:total-units',
        kind: 'total',
        group: 'sales',
        label: 'Total units',
        valueType: 'units',
        favorable: 'higher',
        series: model.sales.totalUnits,
      }),
    );
    for (const p of opts.products
      .filter((p) => p.is_active)
      .sort((a, b) => a.position - b.position)) {
      const bd = model.sales.byProduct.find((b) => b.productId === p.id);
      if (!bd) continue;
      rows.push(
        baseRow({
          key: `prod:${p.id}`,
          kind: 'header',
          group: 'sales',
          label: `${p.name} — units`,
          depth: 1,
          valueType: 'units',
          hasChildren: true,
          favorable: 'higher',
          series: { plan: bd.units, actual: nulls(n) },
        }),
      );
      const prices: Record<CustomerTier, number> = {
        wholesale_big: p.price_big_cents,
        wholesale_small: p.price_small_cents,
        retail: p.price_retail_cents,
      };
      for (const t of TIERS) {
        rows.push(
          baseRow({
            key: `prod:${p.id}:${t}`,
            kind: 'product',
            group: 'sales',
            label: `${TIER_LABEL[t]} units`,
            depth: 2,
            valueType: 'units',
            parentKey: `prod:${p.id}`,
            hint: `@ ${formatCentsShort(prices[t])} each`,
            favorable: 'higher',
            series: { plan: bd.unitsByTier[t], actual: nulls(n) },
          }),
        );
      }
      rows.push(
        baseRow({
          key: `prod:${p.id}:revenue`,
          kind: 'product',
          group: 'sales',
          label: `${p.name} revenue`,
          depth: 2,
          parentKey: `prod:${p.id}`,
          planSource: 'sales',
          favorable: 'higher',
          hint: p.revenue_account_id
            ? `→ ${model.accountsById.get(p.revenue_account_id)?.name ?? 'revenue account'}`
            : 'Not linked to a revenue account',
          series: { plan: bd.revenueCents, actual: nulls(n) },
        }),
      );
      rows.push(
        baseRow({
          key: `prod:${p.id}:cogs`,
          kind: 'product',
          group: 'sales',
          label: `${p.name} COGS`,
          depth: 2,
          parentKey: `prod:${p.id}`,
          planSource: 'cogs',
          favorable: 'lower',
          hint: `@ ${formatCentsShort(p.unit_cogs_cents)} per unit${
            p.cogs_account_id ? ` → ${model.accountsById.get(p.cogs_account_id)?.name ?? ''}` : ''
          }`,
          series: { plan: bd.cogsCents, actual: nulls(n) },
        }),
      );
    }
    if (includePnl) rows.push(spacerRow('spacer:sales', n));
  }

  // ---------------- P&L ----------------
  if (includePnl) {
    const emitPnlSection = (sec: PnlSection) => {
      rows.push(sectionRow(`sec:${sec}`, sec, PNL_SECTION_LABEL[sec], n));
      const roots = model.pnlRoots.filter((r) => r.section === sec);
      for (const root of roots) emitPnlNode(root, 1, sec);
      emitSectionTail(sec, 1);
      rows.push(
        baseRow({
          key: `sectot:${sec}`,
          kind: 'total',
          group: sec,
          label: `Total ${PNL_SECTION_LABEL[sec]}`,
          favorable: sec === 'income' || sec === 'other-income' ? 'higher' : 'lower',
          series: model.sectionTotals[sec],
        }),
      );
    };

    const emitPnlNode = (node: AccountNode, depth: number, sec: PnlSection) => {
      const a = node.account;
      const ov = overrides.get(a.id);
      const total = intr.pnlTotal.get(a.id)!;
      const own = intr.pnlOwn.get(a.id)!;
      const promoted = !!ov && !ov.hidden;
      // `total` already includes every descendant, so a parent stays visible
      // whenever any child carries a number.
      const visible =
        !ov?.hidden && (opts.showAllAccounts || promoted || hasActivity(total, visIdx));
      if (!visible) return;
      const label = ov?.label || a.name;
      const favorable: Favorable = a.account_type === 'revenue' ? 'higher' : 'lower';
      const driven = model.sales.drivenRevenue.has(a.id)
        ? 'sales'
        : model.sales.drivenCogs.has(a.id)
          ? 'cogs'
          : 'manual';
      if (node.children.length > 0) {
        rows.push(
          baseRow({
            key: `hdr:${a.id}`,
            kind: 'header',
            group: sec,
            label,
            depth,
            hasChildren: true,
            accountId: a.id,
            // A parent is editable like any other account: the typed value
            // is the rollup the operator wants, and the difference lands on
            // the parent's own "(direct)" line.
            editablePlan: driven === 'manual',
            planSource: driven,
            planOffset: total.plan.map((v, i) => v - own.plan[i]),
            favorable,
            hint: a.code ?? undefined,
            series: total,
          }),
        );
        if (hasActivity(own, visIdx) || opts.showAllAccounts) {
          rows.push(
            baseRow({
              key: `acct:${a.id}`,
              kind: 'account',
              group: sec,
              label: `${label} (direct)`,
              depth: depth + 1,
              parentKey: `hdr:${a.id}`,
              editablePlan: driven === 'manual',
              planSource: driven,
              favorable,
              accountId: a.id,
              series: own,
            }),
          );
        }
        for (const c of node.children) emitPnlNode(c, depth + 1, sec);
        emitCustoms(`hdr:${a.id}`, sec, depth + 1);
        rows.push(
          baseRow({
            key: `tot:${a.id}`,
            kind: 'total',
            group: sec,
            label: `Total ${label}`,
            depth,
            parentKey: `hdr:${a.id}`,
            accountId: a.id,
            favorable,
            series: total,
          }),
        );
      } else {
        rows.push(
          baseRow({
            key: `acct:${a.id}`,
            kind: 'account',
            group: sec,
            label,
            depth,
            editablePlan: driven === 'manual',
            planSource: driven,
            favorable,
            accountId: a.id,
            hint: a.code ?? undefined,
            series: own,
          }),
        );
        emitCustoms(`acct:${a.id}`, sec, depth);
      }
    };

    emitPnlSection('income');
    emitPnlSection('cogs');
    rows.push(
      baseRow({
        key: 'calc:gross-profit',
        kind: 'calc',
        group: 'summary',
        label: 'Gross Profit',
        favorable: 'higher',
        series: model.grossProfit,
      }),
    );
    rows.push(
      baseRow({
        key: 'ratio:gross-margin',
        kind: 'ratio',
        group: 'summary',
        label: 'Gross margin %',
        depth: 1,
        valueType: 'percent',
        favorable: 'higher',
        series: model.ratios.find((r) => r.key === 'ratio:gross-margin')!.series,
      }),
    );
    emitPnlSection('expense');
    rows.push(
      baseRow({
        key: 'calc:operating-income',
        kind: 'calc',
        group: 'summary',
        label: 'Net Operating Income',
        favorable: 'higher',
        series: model.operatingIncome,
      }),
    );
    emitPnlSection('other-income');
    emitPnlSection('other-expense');
    rows.push(
      baseRow({
        key: 'calc:net-income',
        kind: 'calc',
        group: 'summary',
        label: 'Net Income',
        favorable: 'higher',
        series: model.netIncome,
      }),
    );
    rows.push(
      baseRow({
        key: 'ratio:net-margin',
        kind: 'ratio',
        group: 'summary',
        label: 'Net margin %',
        depth: 1,
        valueType: 'percent',
        favorable: 'higher',
        series: model.ratios.find((r) => r.key === 'ratio:net-margin')!.series,
      }),
    );
  }

  // ---------------- Cash movements outside the P&L ----------------
  if (includeCash) {
    rows.push(spacerRow('spacer:pnl', n));
    const emitCashSection = (sec: CashSection) => {
      rows.push(sectionRow(`sec:${sec}`, sec, CASH_SECTION_LABEL[sec], n));
      const emitNode = (node: AccountNode, depth: number) => {
        const a = node.account;
        if (node.section !== sec) {
          for (const c of node.children) emitNode(c, depth);
          return;
        }
        const ov = overrides.get(a.id);
        const eff = intr.bsEffect.get(a.id)!;
        const subtreeEff = sumSeries(collectEffects(node, intr.bsEffect), n);
        const promoted = !!ov && !ov.hidden;
        const visible =
          !ov?.hidden && (opts.showAllAccounts || promoted || hasActivity(subtreeEff, visIdx));
        if (!visible) return;
        const label = ov?.label || a.name;
        const sign = cashEffectSign(a.account_type);
        const hint =
          a.account_type === 'asset'
            ? '+ cash in = balance down · − cash out = balance up'
            : '+ cash in = balance up · − cash out = balance down';
        if (node.children.length > 0) {
          rows.push(
            baseRow({
              key: `hdr:${a.id}`,
              kind: 'header',
              group: sec,
              label,
              depth,
              hasChildren: true,
              accountId: a.id,
              favorable: 'higher',
              series: subtreeEff,
            }),
          );
          if (hasActivity(eff, visIdx) || opts.showAllAccounts) {
            rows.push(
              baseRow({
                key: `acct:${a.id}`,
                kind: 'account',
                group: sec,
                label: `${label} (direct)`,
                depth: depth + 1,
                parentKey: `hdr:${a.id}`,
                editablePlan: true,
                planSource: 'manual',
                favorable: 'higher',
                accountId: a.id,
                effectSign: sign,
                hint,
                series: eff,
              }),
            );
          }
          for (const c of node.children) emitNode(c, depth + 1);
          emitCustoms(`hdr:${a.id}`, sec, depth + 1);
          rows.push(
            baseRow({
              key: `tot:${a.id}`,
              kind: 'total',
              group: sec,
              label: `Total ${label}`,
              depth,
              parentKey: `hdr:${a.id}`,
              accountId: a.id,
              favorable: 'higher',
              series: subtreeEff,
            }),
          );
        } else {
          rows.push(
            baseRow({
              key: `acct:${a.id}`,
              kind: 'account',
              group: sec,
              label,
              depth,
              editablePlan: true,
              planSource: 'manual',
              favorable: 'higher',
              accountId: a.id,
              effectSign: sign,
              hint,
              series: eff,
            }),
          );
          emitCustoms(`acct:${a.id}`, sec, depth);
        }
      };
      for (const root of model.bsRoots) emitNode(root, 1);
      if (sec === 'investing' && model.capitalEffect.plan.some((v) => v)) {
        rows.push(
          baseRow({
            key: 'calc:capital-plan',
            kind: 'calc',
            group: sec,
            label: 'Capital plan items',
            depth: 1,
            planSource: 'capital',
            favorable: 'higher',
            hint: 'From the Capital Planning tab',
            series: model.capitalEffect,
          }),
        );
      }
      emitSectionTail(sec, 1);
      const total = { ...model.cashEffects[sec] };
      if (sec === 'investing') {
        total.plan = total.plan.map((v, i) => v + model.capitalEffect.plan[i]);
      }
      rows.push(
        baseRow({
          key: `sectot:${sec}`,
          kind: 'total',
          group: sec,
          label: `Net cash from ${sec}`,
          favorable: 'higher',
          series: total,
        }),
      );
    };
    emitCashSection('operating');
    emitCashSection('investing');
    emitCashSection('financing');

    rows.push(spacerRow('spacer:cash', n));
    rows.push(sectionRow('sec:cash-summary', 'cash', 'Month-to-month cash flow', n));
    rows.push(
      baseRow({
        key: 'calc:ni-ref',
        kind: 'calc',
        group: 'cash',
        label: 'Net Income',
        depth: 1,
        favorable: 'higher',
        series: model.netIncome,
      }),
    );
    rows.push(
      baseRow({
        key: 'calc:bs-moves',
        kind: 'calc',
        group: 'cash',
        label: 'Balance sheet movements (net cash effect)',
        depth: 1,
        favorable: 'higher',
        series: {
          plan: model.cashFlow.plan.map((v, i) => v - model.netIncome.plan[i]),
          actual: model.cashFlow.actual.map((v, i) =>
            v === null ? null : v - (model.netIncome.actual[i] ?? 0),
          ),
        },
      }),
    );
    rows.push(
      baseRow({
        key: 'calc:cash-flow',
        kind: 'total',
        group: 'cash',
        label: 'Net cash flow for the month',
        favorable: 'higher',
        series: model.cashFlow,
      }),
    );
    rows.push(
      baseRow({
        key: 'calc:opening-cash',
        kind: 'calc',
        group: 'cash',
        label: 'Opening bank balance',
        depth: 1,
        favorable: 'higher',
        series: model.openingCash,
      }),
    );
    rows.push(
      baseRow({
        key: 'calc:ending-cash',
        kind: 'calc',
        group: 'cash',
        label: 'Ending bank balance',
        favorable: 'higher',
        hint: 'Actual = QuickBooks bank accounts at month end',
        series: model.endingCash,
      }),
    );
    rows.push(
      baseRow({
        key: 'calc:net-cash-after-cc',
        kind: 'calc',
        group: 'cash',
        label: 'Bank balance less credit cards',
        depth: 1,
        favorable: 'higher',
        series: {
          plan: model.endingCash.plan.map((v, i) => {
            // Plan CC balance = last actual CC balance carried forward with
            // any planned movements; approximate with the derived BS plan.
            const ccPlan = ccPlanAt(model, i);
            return v - ccPlan;
          }),
          actual: model.endingCash.actual.map((v, i) => {
            const cc = model.creditCardBalance[i];
            if (v === null) return null;
            return v - (cc ?? 0);
          }),
        },
      }),
    );
    for (const chk of model.checks) {
      rows.push(
        baseRow({
          key: chk.key,
          kind: 'check',
          group: 'checks',
          label: `Check — ${chk.label}`,
          depth: 1,
          hint: chk.description,
          series: { plan: zeros(n), actual: chk.diffs },
        }),
      );
    }
  }

  // ---------------- Ratios (P&L only sheets get them at the bottom) ----------------
  if (opts.sheet === 'pnl') {
    rows.push(spacerRow('spacer:ratios', n));
    rows.push(sectionRow('sec:ratios', 'ratios', 'Ratios — % of revenue', n));
    for (const r of model.ratios) {
      if (r.key === 'ratio:gross-margin' || r.key === 'ratio:net-margin') continue;
      rows.push(
        baseRow({
          key: r.key,
          kind: 'ratio',
          group: 'ratios',
          label: r.label,
          depth: 1,
          valueType: 'percent',
          favorable: r.favorable,
          series: r.series,
        }),
      );
    }
  }

  return rows;
}

function ccPlanAt(model: Model, i: number): number {
  const intr = internals.get(model)!;
  let total = 0;
  for (const id of intr.bsIsCreditCard) total += model.bsPlanBalances.get(id)?.[i] ?? 0;
  return total;
}

function collectEffects(node: AccountNode, effects: Map<string, Series>): Series[] {
  const out: Series[] = [effects.get(node.account.id)!];
  for (const c of node.children) out.push(...collectEffects(c, effects));
  return out;
}

// ---------------- Balance sheet ----------------

function buildBalanceSheetRows(model: Model, opts: SheetBuildOptions): EngineRow[] {
  const n = model.months.length;
  const visIdx = opts.visibleMonths.map((m) => model.months.indexOf(m)).filter((i) => i >= 0);
  const intr = internals.get(model)!;
  const rows: EngineRow[] = [];

  const seriesFor = (id: string): Series => ({
    plan: model.bsPlanBalances.get(id) ?? zeros(n),
    actual: model.bsActualBalances.get(id) ?? nulls(n),
  });
  const subtree = (node: AccountNode): Series => {
    const s = emptySeries(n);
    addSeries(s, seriesFor(node.account.id));
    for (const c of node.children) addSeries(s, subtree(c));
    return s;
  };

  const emitNode = (node: AccountNode, depth: number, group: string) => {
    const a = node.account;
    const total = subtree(node);
    if (!opts.showAllAccounts && !hasActivity(total, visIdx)) return;
    const favorable: Favorable =
      a.account_type === 'asset' ? 'higher' : a.account_type === 'liability' ? 'lower' : 'higher';
    if (node.children.length > 0) {
      rows.push(
        baseRow({
          key: `hdr:${a.id}`,
          kind: 'header',
          group,
          label: a.name,
          depth,
          hasChildren: true,
          accountId: a.id,
          favorable,
          series: total,
        }),
      );
      if (hasActivity(seriesFor(a.id), visIdx) || opts.showAllAccounts) {
        rows.push(
          baseRow({
            key: `acct:${a.id}`,
            kind: 'account',
            group,
            label: `${a.name} (direct)`,
            depth: depth + 1,
            parentKey: `hdr:${a.id}`,
            accountId: a.id,
            favorable,
            series: seriesFor(a.id),
          }),
        );
      }
      for (const c of node.children) emitNode(c, depth + 1, group);
      rows.push(
        baseRow({
          key: `tot:${a.id}`,
          kind: 'total',
          group,
          label: `Total ${a.name}`,
          depth,
          parentKey: `hdr:${a.id}`,
          accountId: a.id,
          favorable,
          series: total,
        }),
      );
    } else {
      const isBank = intr.bsIsBank.has(a.id);
      rows.push(
        baseRow({
          key: `acct:${a.id}`,
          kind: 'account',
          group,
          label: a.name,
          depth,
          accountId: a.id,
          favorable,
          hint: isBank
            ? 'Plan shown on the bank total — projected from the Main sheet'
            : (a.code ?? undefined),
          series: isBank ? { plan: zeros(n), actual: seriesFor(a.id).actual } : seriesFor(a.id),
        }),
      );
    }
  };

  const bsSubs: BsSubSection[] = [
    'bank',
    'ar',
    'other-current-asset',
    'fixed-asset',
    'other-asset',
    'ap',
    'credit-card',
    'other-current-liability',
    'long-term-liability',
    'equity',
  ];
  const totalsBySub = new Map<BsSubSection, Series>();
  for (const sub of bsSubs) {
    const roots = model.bsRoots.filter((r) => r.bsSub === sub);
    const total = sumSeries(roots.map(subtree), n);
    if (sub === 'bank') {
      // Plan for bank accounts is the projected ending cash (not per-account).
      total.plan = model.endingCash.plan.slice();
    }
    totalsBySub.set(sub, total);
  }

  const assets = emptySeries(n);
  const liabilities = emptySeries(n);
  const equity = emptySeries(n);

  // Capital-plan items that are not tied to a QBO account still consume
  // cash, so the projected balance sheet carries them as an unallocated
  // fixed asset (cumulative) to stay in balance.
  const capitalAsset = emptySeries(n);
  let cum = 0;
  for (let i = 0; i < n; i++) {
    cum += -model.capitalEffect.plan[i];
    capitalAsset.plan[i] = cum;
  }
  const hasCapital = capitalAsset.plan.some((v) => v !== 0);

  rows.push(sectionRow('sec:assets', 'assets', 'Assets', n));
  for (const sub of bsSubs.slice(0, 5)) {
    const roots = model.bsRoots.filter((r) => r.bsSub === sub);
    const total = totalsBySub.get(sub)!;
    const withCapital = sub === 'fixed-asset' && hasCapital;
    if (withCapital) addSeries(total, capitalAsset);
    if (
      (roots.length === 0 && !withCapital) ||
      (!opts.showAllAccounts && !hasActivity(total, visIdx))
    )
      continue;
    rows.push(
      baseRow({
        key: `hdr:sub:${sub}`,
        kind: 'header',
        group: 'assets',
        label: BS_SUB_LABEL[sub],
        depth: 1,
        hasChildren: true,
        favorable: 'higher',
        series: total,
      }),
    );
    for (const r of roots) emitNode(r, 2, 'assets');
    if (withCapital) {
      rows.push(
        baseRow({
          key: 'calc:capital-plan-asset',
          kind: 'calc',
          group: 'assets',
          label: 'Capital plan items (unallocated)',
          depth: 2,
          parentKey: `hdr:sub:${sub}`,
          planSource: 'capital',
          favorable: 'higher',
          hint: 'Cumulative capital-plan spend not yet tied to a QuickBooks account',
          series: capitalAsset,
        }),
      );
    }
    rows.push(
      baseRow({
        key: `tot:sub:${sub}`,
        kind: 'total',
        group: 'assets',
        label: `Total ${BS_SUB_LABEL[sub]}`,
        depth: 1,
        parentKey: `hdr:sub:${sub}`,
        favorable: 'higher',
        series: total,
      }),
    );
    addSeries(assets, total);
  }
  rows.push(
    baseRow({
      key: 'calc:total-assets',
      kind: 'calc',
      group: 'assets',
      label: 'Total Assets',
      favorable: 'higher',
      series: assets,
    }),
  );

  rows.push(spacerRow('spacer:assets', n));
  rows.push(sectionRow('sec:liabilities', 'liabilities', 'Liabilities', n));
  for (const sub of bsSubs.slice(5, 9)) {
    const roots = model.bsRoots.filter((r) => r.bsSub === sub);
    const total = totalsBySub.get(sub)!;
    if (roots.length === 0 || (!opts.showAllAccounts && !hasActivity(total, visIdx))) continue;
    rows.push(
      baseRow({
        key: `hdr:sub:${sub}`,
        kind: 'header',
        group: 'liabilities',
        label: BS_SUB_LABEL[sub],
        depth: 1,
        hasChildren: true,
        favorable: 'lower',
        series: total,
      }),
    );
    for (const r of roots) emitNode(r, 2, 'liabilities');
    rows.push(
      baseRow({
        key: `tot:sub:${sub}`,
        kind: 'total',
        group: 'liabilities',
        label: `Total ${BS_SUB_LABEL[sub]}`,
        depth: 1,
        parentKey: `hdr:sub:${sub}`,
        favorable: 'lower',
        series: total,
      }),
    );
    addSeries(liabilities, total);
  }
  rows.push(
    baseRow({
      key: 'calc:total-liabilities',
      kind: 'calc',
      group: 'liabilities',
      label: 'Total Liabilities',
      favorable: 'lower',
      series: liabilities,
    }),
  );

  rows.push(spacerRow('spacer:liabilities', n));
  rows.push(sectionRow('sec:equity', 'equity', 'Equity', n));
  {
    const roots = model.bsRoots.filter((r) => r.bsSub === 'equity');
    for (const r of roots) {
      // Retained earnings: plan is the carried-forward figure.
      if (intr.bsIsRe.has(r.account.id) && r.children.length === 0) {
        const s = seriesFor(r.account.id);
        const series = { plan: model.retainedEarningsPlan, actual: s.actual };
        if (!opts.showAllAccounts && !hasActivity(series, visIdx)) continue;
        rows.push(
          baseRow({
            key: `acct:${r.account.id}`,
            kind: 'account',
            group: 'equity',
            label: r.account.name,
            depth: 1,
            accountId: r.account.id,
            favorable: 'higher',
            series,
          }),
        );
        addSeries(equity, series);
        continue;
      }
      emitNode(r, 1, 'equity');
      addSeries(equity, subtree(r));
    }
    rows.push(
      baseRow({
        key: 'calc:net-income-ytd',
        kind: 'calc',
        group: 'equity',
        label: 'Net Income (fiscal year to date)',
        depth: 1,
        favorable: 'higher',
        hint: 'Derived from the P&L — QuickBooks reports this line without an account',
        series: model.netIncomeYtd,
      }),
    );
    addSeries(equity, model.netIncomeYtd);
  }
  rows.push(
    baseRow({
      key: 'calc:total-equity',
      kind: 'calc',
      group: 'equity',
      label: 'Total Equity',
      favorable: 'higher',
      series: equity,
    }),
  );
  const liabEq = sumSeries([liabilities, equity], n);
  rows.push(
    baseRow({
      key: 'calc:total-liab-equity',
      kind: 'calc',
      group: 'equity',
      label: 'Total Liabilities & Equity',
      favorable: 'none',
      series: liabEq,
    }),
  );
  rows.push(
    baseRow({
      key: 'check:bs-sheet',
      kind: 'check',
      group: 'checks',
      label: 'Check — Assets − (Liabilities + Equity) should be $0',
      depth: 1,
      hint: 'Plan side balances by construction; an actual gap means QuickBooks itself is out of balance for that month.',
      series: subSeries(assets, liabEq),
    }),
  );
  return rows;
}

// ---------------- Cash flow statement ----------------

function buildCashflowRows(model: Model, opts: SheetBuildOptions): EngineRow[] {
  const n = model.months.length;
  const visIdx = opts.visibleMonths.map((m) => model.months.indexOf(m)).filter((i) => i >= 0);
  const intr = internals.get(model)!;
  const rows: EngineRow[] = [];

  rows.push(sectionRow('sec:operating', 'operating', 'Operating Activities', n));
  rows.push(
    baseRow({
      key: 'calc:net-income',
      kind: 'calc',
      group: 'operating',
      label: 'Net Income',
      depth: 1,
      favorable: 'higher',
      series: model.netIncome,
    }),
  );
  rows.push(
    baseRow({
      key: 'hdr:adjustments',
      kind: 'header',
      group: 'operating',
      label: 'Adjustments to reconcile Net Income to cash',
      depth: 1,
      hasChildren: true,
      favorable: 'higher',
      series: model.cashEffects.operating,
    }),
  );
  const emitLeaves = (sec: CashSection, depth: number) => {
    walk(model.bsRoots, (node) => {
      if (node.section !== sec) return;
      const eff = intr.bsEffect.get(node.account.id)!;
      if (!opts.showAllAccounts && !hasActivity(eff, visIdx)) return;
      rows.push(
        baseRow({
          key: `acct:${node.account.id}`,
          kind: 'account',
          group: sec,
          label: node.account.name,
          depth,
          parentKey: sec === 'operating' ? 'hdr:adjustments' : undefined,
          accountId: node.account.id,
          favorable: 'higher',
          hint: `${node.account.account_type} — ${node.account.account_type === 'asset' ? 'increase uses cash' : 'increase provides cash'}`,
          series: eff,
        }),
      );
    });
  };
  emitLeaves('operating', 2);
  rows.push(
    baseRow({
      key: 'tot:adjustments',
      kind: 'total',
      group: 'operating',
      label: 'Total adjustments',
      depth: 1,
      parentKey: 'hdr:adjustments',
      favorable: 'higher',
      series: model.cashEffects.operating,
    }),
  );
  rows.push(
    baseRow({
      key: 'sectot:operating',
      kind: 'total',
      group: 'operating',
      label: 'Net cash provided by operating activities',
      favorable: 'higher',
      series: sumSeries([model.netIncome, model.cashEffects.operating], n),
    }),
  );

  rows.push(spacerRow('spacer:op', n));
  rows.push(sectionRow('sec:investing', 'investing', 'Investing Activities', n));
  emitLeaves('investing', 1);
  if (model.capitalEffect.plan.some((v) => v)) {
    rows.push(
      baseRow({
        key: 'calc:capital-plan',
        kind: 'calc',
        group: 'investing',
        label: 'Capital plan items',
        depth: 1,
        planSource: 'capital',
        favorable: 'higher',
        series: model.capitalEffect,
      }),
    );
  }
  rows.push(
    baseRow({
      key: 'sectot:investing',
      kind: 'total',
      group: 'investing',
      label: 'Net cash provided by investing activities',
      favorable: 'higher',
      series: {
        plan: model.cashEffects.investing.plan.map((v, i) => v + model.capitalEffect.plan[i]),
        actual: model.cashEffects.investing.actual,
      },
    }),
  );

  rows.push(spacerRow('spacer:inv', n));
  rows.push(sectionRow('sec:financing', 'financing', 'Financing Activities', n));
  emitLeaves('financing', 1);
  rows.push(
    baseRow({
      key: 'sectot:financing',
      kind: 'total',
      group: 'financing',
      label: 'Net cash provided by financing activities',
      favorable: 'higher',
      series: model.cashEffects.financing,
    }),
  );

  rows.push(spacerRow('spacer:fin', n));
  rows.push(sectionRow('sec:net', 'cash', 'Net change in cash', n));
  rows.push(
    baseRow({
      key: 'calc:cash-flow',
      kind: 'total',
      group: 'cash',
      label: 'Net cash increase (decrease) for the period',
      favorable: 'higher',
      series: model.cashFlow,
    }),
  );
  rows.push(
    baseRow({
      key: 'calc:opening-cash',
      kind: 'calc',
      group: 'cash',
      label: 'Cash at beginning of period',
      depth: 1,
      favorable: 'higher',
      series: model.openingCash,
    }),
  );
  rows.push(
    baseRow({
      key: 'calc:ending-cash',
      kind: 'calc',
      group: 'cash',
      label: 'Cash at end of period',
      favorable: 'higher',
      hint: 'Actual = QuickBooks bank balances',
      series: model.endingCash,
    }),
  );
  const cashCheck = model.checks.find((c) => c.key === 'check:cash');
  if (cashCheck) {
    rows.push(
      baseRow({
        key: cashCheck.key,
        kind: 'check',
        group: 'checks',
        label: `Check — ${cashCheck.label}`,
        depth: 1,
        hint: cashCheck.description,
        series: { plan: zeros(n), actual: cashCheck.diffs },
      }),
    );
  }
  return rows;
}

// ---------------------------------------------------------------------------
// Formatting helper used in hints (kept here so the engine stays alias-free).
// ---------------------------------------------------------------------------

export function formatCentsShort(cents: number): string {
  const dollars = cents / 100;
  if (Number.isInteger(dollars)) return `$${dollars.toLocaleString('en-US')}`;
  return `$${dollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ---------------------------------------------------------------------------
// Conditional-formatting decisions — centralised so the grid and the AI
// summary flag the same cells.
// ---------------------------------------------------------------------------

export type Alarm = 'none' | 'warn' | 'bad' | 'good';

export function varianceAlarm(
  row: Pick<EngineRow, 'favorable' | 'valueType' | 'kind'>,
  plan: number,
  actual: number | null,
  settings: WorkbookSettings,
): Alarm {
  if (actual === null || row.favorable === 'none' || row.kind === 'check') return 'none';
  if (row.valueType === 'percent') {
    const diff = actual - plan;
    const bad = row.favorable === 'higher' ? diff < -0.05 : diff > 0.05;
    const good = row.favorable === 'higher' ? diff > 0.05 : diff < -0.05;
    return bad ? 'bad' : good ? 'good' : 'none';
  }
  const diff = actual - plan;
  const abs = Math.abs(diff);
  const pct = plan !== 0 ? abs / Math.abs(plan) : actual !== 0 ? 1 : 0;
  const material =
    row.valueType === 'currency'
      ? abs >= settings.variance_abs_cents && pct >= settings.variance_pct_threshold
      : pct >= settings.variance_pct_threshold && abs >= 1;
  if (!material) return 'none';
  const unfavorable = row.favorable === 'higher' ? diff < 0 : diff > 0;
  return unfavorable ? (pct >= settings.variance_pct_threshold * 2.5 ? 'bad' : 'warn') : 'good';
}

export function cashAlarm(ending: number, settings: WorkbookSettings): Alarm {
  if (ending < 0) return 'bad';
  if (ending < settings.min_cash_cents) return 'warn';
  return 'none';
}

export function checkAlarm(diff: number | null, tolerance: number): Alarm {
  if (diff === null) return 'none';
  return Math.abs(diff) <= tolerance ? 'good' : 'bad';
}
